// ── Állapot-besorolás: EGY forrás-igazság (CRO audit, 1. feladat) ────────────
// A DB grade-mezőit NEM migráljuk – a normalizálás kizárólag DISPLAY-szinten
// történik (normalizeGrade). A kanonikus sorrend a vevő számára: fentről lefelé
// egyre több használati nyom.

/**
 * A fokozatok jelentésének KÖZÖS kiegészítése – EGY forrásból.
 *
 * Minden grade-badge magyarázatához hozzáfűzzük, hogy a látogató ne higgye:
 * a „Jó” fokozat rosszabbul MŰKÖDŐ telefont jelent. Azért itt van és nem
 * oldalanként, hogy ne lehessen egyik felületen frissíteni, a másikon nem.
 */
export const GRADE_COSMETIC_NOTE =
  'A fokozat csak a készülék esztétikai állapotát jelenti — működésben az összes fokozat hibátlan.';

export const GRADES = {
  BONTATLAN: {
    key: 'BONTATLAN',
    label: 'Bontatlan',
    definition: 'Gyári fóliás, bontatlan.', // csak új kiegészítőre
    // A `color` a badge 8×8 px-es jelölő pöttyére megy (a CÍMKE szövegszíne
    // #333333, azt nem érinti). Nem-szöveg elemre a WCAG 1.4.11 3:1-et kér
    // fehéren; a korábbi #39B54A csak 2,66:1 volt – halvány, kis méretben
    // alig látszott. A #2A9539 ugyanaz a zöld család, 3,85:1.
    color: '#2A9539',
    order: 0,
  },
  UJSZERU: {
    key: 'UJSZERU',
    label: 'Újszerű',
    definition: 'Szinte észrevehetetlen nyomok — ránézésre új.',
    color: '#2A9539',   // lásd BONTATLAN: 2,66:1 → 3,85:1
    order: 1,
  },
  KIVALO: {
    key: 'KIVALO',
    label: 'Kiváló',
    definition: 'Kisebb esztétikai nyomok, a működést nem érintik.',
    color: '#2F8E3B',
    order: 2,
  },
  JO: {
    key: 'JO',
    label: 'Jó',
    definition: 'Látható nyomok, a kijelzőt és a működést nem érintik.',
    color: '#8A8A8A',
    order: 3,
  },
};

// Nyers érték (office-kód VAGY magyar címke) → kanonikus grade.
// Ismeretlen értéknél null-t adunk vissza (semleges fallback badge), és a
// nyers értéket összegyűjtjük, hogy emberi döntés születhessen róla – NEM
// tippelünk.
const RAW_MAP = {
  // office condition-kódok
  new: 'BONTATLAN',
  like_new: 'UJSZERU',
  nice: 'KIVALO',
  used: 'JO',
  // magyar címkék (régi/kevert DB-adat)
  'új': 'BONTATLAN',
  'bontatlan': 'BONTATLAN',
  'újszerű': 'UJSZERU',
  'szép állapot': 'KIVALO',
  'kiváló': 'KIVALO',
  'használt': 'JO',
  'jó': 'JO',
};

/** Futás közben talált ismeretlen grade-értékek (display-oldali gyűjtés). */
export const unknownGrades = new Set();

export function normalizeGrade(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  const key = String(raw).trim().toLowerCase();
  const canonical = RAW_MAP[key];
  if (canonical) return GRADES[canonical];
  if (!unknownGrades.has(String(raw))) {
    unknownGrades.add(String(raw));
    // Fejlesztői segéd – éles UI-t nem érinti, a semleges fallback badge megy ki.
    // eslint-disable-next-line no-console
    console.warn('[grades] Ismeretlen állapot-érték (döntést igényel):', raw);
  }
  return null;
}

// ── Akkumulátor-egészség: mely kategóriáknál értelmezett ────────────────────
// Ezeknél a készülékeknél a „hány százalék az akku” kérdés NEM értelmes: az
// AirPods/AirTag/Pencil egészségét nem mérjük, a konzolban nincs akku.
// A hiba forrása az volt, hogy a `mapDevice` a specifikáció szövegéből is
// kiolvas egy „%”-ot, ha a `battery_health` üres – így AirPods-nál is
// megjelent egy szám, aminek semmi köze az akkumulátorhoz.
const NO_BATTERY_CATEGORIES = new Set(['AirPods', 'AirTag', 'Apple Pencil', 'Konzol']);

/** Értelmezett-e az akkumulátor-egészség ennél a kategóriánál? */
export const hasBatteryReading = (category) => !NO_BATTERY_CATEGORIES.has(category);

/**
 * Akku-érték kategóriára szűrve – EZ az egyetlen kapu.
 *
 * Az adat MAPPELÉSEKOR kell hívni, nem renderelésnél: így egyszerre javul a
 * badge, a modál specifikáció-sora, az adatlap, a szűrő, a rendezés ÉS a
 * strukturált adat (JSON-LD) – nem kell N helyen ugyanazt a feltételt őrizni.
 */
export const batteryForCategory = (battery, category) =>
  (hasBatteryReading(category) ? (battery ?? null) : null);

/**
 * Az office-rekord akku-értéke („92%” vagy null), kategóriára szűrve.
 * A lista ÉS a termékoldal is ezt hívja – korábban a lista a specifikáció
 * szövegéből is kiolvasta a %-ot, a termékoldal nem, így ugyanaz a darab a
 * listán akkut mutatott, a termékoldalon nem.
 * Sorrend: `battery_health` (> 0), különben az első „NN%” a specifikációban.
 */
export const officeBattery = (d = {}, category) => {
  const n = Number(d.battery_health);
  const nyers = n > 0
    ? `${n}%`
    : (() => { const m = String(d.specification || '').match(/(\d+)\s*%/); return m ? `${m[1]}%` : null; })();
  return batteryForCategory(nyers, category);
};

// Kiegészítő-kategóriák (megjelenítési célra). A `hasBatteryReading` NEM ebből
// dolgozik: a konzol nem kiegészítő, akkuja viszont nincs.
const ACCESSORY_CATEGORIES = new Set(['AirPods', 'AirTag', 'Apple Pencil']);
export const isAccessoryCategory = (category) => ACCESSORY_CATEGORIES.has(category);
